import { useState } from 'react';
import { HiChevronDown, HiChevronUp } from 'react-icons/hi2';
import type { CalculatorResult } from './calculatorTypes';
import type { CalcTexts } from './locales';
import { kids } from './calculatorUtils';
import styles from './Calculator.module.css';

function toNum(v: string): number {
  const n = parseInt(v, 10);
  return isNaN(n) ? 0 : n;
}

function euro(v: number): string {
  return v.toLocaleString('de-DE') + ' €';
}

interface Props {
  result: CalculatorResult;
  texts: CalcTexts;
}

export function CalculatorResultCard({ result, texts }: Props) {
  const [showDetails, setShowDetails] = useState(false);
  const { grundsicherung, bedarf, totalAnrechenbares, formData } = result;

  const {
    shared_household,
    pregnant,
    children_0_5, children_6_13, children_14_17, children_18_24_non_working,
    child_1_age, child_2_age,
    checkbox_income_user, income_user_gross, income_user_net,
    checkbox_income_partner, income_partner_gross, income_partner_net,
    checkbox_other_income, rental_income, unemployment_benefit, pension, other_income_amount,
    checkbox_housing_costs, cold_rent, additional_costs, heating_costs,
  } = formData;

  const childrenTotal =
    children_0_5 + children_6_13 + children_14_17 + children_18_24_non_working;

  return (
    <div className={styles.resultCard}>
      <div>
        <p className={styles.resultTitle}>{texts.result.title}</p>
        <p className={styles.resultSubtitle}>{texts.result.potential_entitlement_intro}</p>
      
        <button
          type="button"
          className={styles.resultToggle}
          onClick={() => setShowDetails(v => !v)}
          aria-expanded={showDetails}
        >
          {showDetails ? texts.result.hide_details : texts.result.show_details}
          {showDetails ? HiChevronUp({}) : HiChevronDown({})}
        </button>

        {showDetails && (
          <ul className={styles.resultSummaryCollapse}>
            {shared_household && (
              <li>{texts.summary.values.lives_with_partner}</li>
            )}
            {childrenTotal > 0 && (
              <>
                {children_0_5 > 0 && <li>{kids(children_0_5, texts)} ({texts.common.age_groups.age_0_5})</li>}
                {children_6_13 > 0 && <li>{kids(children_6_13, texts)} ({texts.common.age_groups.age_6_13})</li>}
                {children_14_17 > 0 && <li>{kids(children_14_17, texts)} ({texts.common.age_groups.age_14_17})</li>}
                {children_18_24_non_working > 0 && <li>{kids(children_18_24_non_working, texts)} ({texts.common.age_groups.age_18_24_non_working})</li>}
                {child_1_age != null && <li>{texts.child_ages.child_1_age_summary.replace('{age}', String(child_1_age))}</li>}
                {child_2_age != null && <li>{texts.child_ages.child_2_age_summary.replace('{age}', String(child_2_age))}</li>}
              </>
            )}
            {pregnant && (
              <li>{shared_household
                ? texts.summary.values.pregnant_or_partner_pregnant_from_week_13
                : texts.summary.values.pregnant_from_week_13}
              </li>
            )}
            {!checkbox_income_user && toNum(income_user_gross) > 0 && (
              <li>{texts.summary.labels.user_gross_income}: {euro(toNum(income_user_gross))}</li>
            )}
            {!checkbox_income_user && toNum(income_user_net) > 0 && (
              <li>{texts.summary.labels.user_net_income}: {euro(toNum(income_user_net))}</li>
            )}
            {shared_household && !checkbox_income_partner && toNum(income_partner_gross) > 0 && (
              <li>{texts.summary.labels.partner_gross_income}: {euro(toNum(income_partner_gross))}</li>
            )}
            {shared_household && !checkbox_income_partner && toNum(income_partner_net) > 0 && (
              <li>{texts.summary.labels.partner_net_income}: {euro(toNum(income_partner_net))}</li>
            )}
            {!checkbox_other_income && toNum(rental_income) > 0 && (
              <li>{texts.other_income.rental_income}: {euro(toNum(rental_income))}</li>
            )}
            {!checkbox_other_income && toNum(unemployment_benefit) > 0 && (
              <li>{texts.other_income.unemployment_benefit_i}: {euro(toNum(unemployment_benefit))}</li>
            )}
            {!checkbox_other_income && toNum(pension) > 0 && (
              <li>{texts.other_income.pension}: {euro(toNum(pension))}</li>
            )}
            {!checkbox_other_income && toNum(other_income_amount) > 0 && (
              <li>{texts.other_income.other}: {euro(toNum(other_income_amount))}</li>
            )}
            {!checkbox_housing_costs && toNum(cold_rent) > 0 && (
              <li>{texts.housing.cold_rent}: {euro(toNum(cold_rent))}</li>
            )}
            {!checkbox_housing_costs && toNum(additional_costs) > 0 && (
              <li>{texts.housing.additional_costs}: {euro(toNum(additional_costs))}</li>
            )}
            {!checkbox_housing_costs && toNum(heating_costs) > 0 && (
              <li>{texts.housing.heating_costs}: {euro(toNum(heating_costs))}</li>
            )}
          </ul>
        )}
      </div>

      <div className={styles.resultAmountWrapper}>
        <p className={styles.resultAmount}>{euro(grundsicherung)}<sup>*</sup></p>
        <p className={styles.resultAmountLabel}>{texts.result.grundsicherung_per_month}</p>
      </div>

      <div className={styles.resultDetails}>
        <span>{texts.result.monthly_needs}: {euro(bedarf)}</span>
        <span>{texts.result.counted_income}: {euro(totalAnrechenbares)}</span>
      </div>

      <p className={styles.resultDisclaimer}><sup>*</sup>{texts.result.disclaimer}</p>
    </div>
  );
}
